import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton, Menu,
  MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, FormControl, InputLabel, Select, TextField, Tooltip,
  alpha, useTheme, Divider, Stack,
} from '@mui/material';
import {
  Add, MoreVert, Edit, Delete, Refresh, Assignment, Block, Close,
  LabelOutlined,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';
import { useModulePermissions } from '../hooks/useModulePermissions';
import letterTemplateService from '../services/letterTemplateService';
import letterCategoryService from '../services/letterCategoryService';
import { centerService } from '../services/centerService';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import { Center } from '../types';
import { LetterCategory, LetterTemplateV2, LetterTemplateV2FormData, LetterCategoryRef } from '../types/letters';
import { severityChipSx } from '../utils/severityTone';

const templateSchema = yup.object({
  name: yup.string().required('Template name is required').max(120),
  categoryId: yup.string().required('Category is required'),
  content: yup.string().required('Content is required'),
});

/**
 * Placeholders available everywhere — student / school / session data is
 * pulled from the student's active enrollment at issuance time.
 */
const COMMON_PLACEHOLDERS = [
  { group: 'Student Details', items: ['{STUDENT_NAME}', '{ROLL_NUMBER}', '{FATHER_NAME}', '{STUDENT_PHONE}', '{FATHER_PHONE}', '{ADDRESS}'] },
  { group: 'School Details',  items: ['{SCHOOL_NAME}'] },
  { group: 'Academic Session', items: ['{SESSION_NAME}', '{CLASS_NAME}', '{SECTION_NAME}'] },
];

/**
 * Letter-detail placeholders depend on the category — using {TITLE} in a Fine
 * template would render as blank because the Fine issue form only collects
 * `reason` + `amount`. We compute the offered list from the category's flags
 * so authors can't write placeholders that never get filled in.
 */
const buildLetterPlaceholders = (cat?: LetterCategory | null): string[] => {
  const items = ['{DATE}', '{DESCRIPTION}'];
  if (!cat) {
    // No category picked yet — show everything so users can still write a
    // generic template, but the picked category later constrains the issued
    // letter's content.
    return ['{DATE}', '{TITLE}', '{REASON}', '{DESCRIPTION}', '{AMOUNT}'];
  }
  if (cat.requiresReason) items.push('{REASON}');
  else items.push('{TITLE}');
  if (cat.requiresAmount) items.push('{AMOUNT}');
  // Keep stable order: DATE first, then category-specific, then DESCRIPTION last.
  return ['{DATE}', ...items.filter(p => p !== '{DATE}' && p !== '{DESCRIPTION}'), '{DESCRIPTION}'];
};

interface FormState extends LetterTemplateV2FormData {
  schoolId?: string;
}

const LettersPage: React.FC = () => {
  const theme = useTheme();
  const { user, loading: authLoading } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();

  const [categories, setCategories] = useState<LetterCategory[]>([]);
  const categoryById = useMemo(() => {
    const m: Record<string, LetterCategory> = {};
    for (const c of categories) m[c._id] = c;
    return m;
  }, [categories]);

  const [templates, setTemplates] = useState<LetterTemplateV2[]>([]);
  const [centers, setCenters] = useState<Center[]>([]);
  const [selectedCenter, setSelectedCenter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LetterTemplateV2 | null>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<LetterTemplateV2 | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isSystemAdmin = user?.isSystemAdmin || false;

  const loadCategories = useCallback(async () => {
    try {
      const list = await letterCategoryService.listLetterCategories();
      setCategories(list);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to load categories', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadTemplates = useCallback(async () => {
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (isSystemAdmin && selectedCenter) params.schoolId = selectedCenter;
      if (categoryFilter) params.categoryId = categoryFilter;
      const res = await letterTemplateService.getLetterTemplates(params);
      setTemplates(res.templates);
      setTotalCount(res.pagination.totalItems);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to load templates', { variant: 'error' });
    }
  }, [page, rowsPerPage, categoryFilter, isSystemAdmin, selectedCenter, enqueueSnackbar]);

  const loadCenters = useCallback(async () => {
    if (authLoading || !isSystemAdmin) return;
    try {
      const res = await centerService.getCenters();
      if (res) setCenters(res.centers);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to load centers', { variant: 'error' });
    }
  }, [authLoading, isSystemAdmin, enqueueSnackbar]);

  useEffect(() => { loadCenters(); }, [loadCenters]);
  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const formik = useFormik<FormState>({
    enableReinitialize: true,
    initialValues: {
      name: editing?.name || '',
      categoryId: typeof editing?.categoryId === 'string' ? editing.categoryId : (editing?.categoryId as LetterCategoryRef)?._id || '',
      content: editing?.content || '',
      schoolId: typeof editing?.schoolId === 'string'
        ? editing.schoolId
        : (editing?.schoolId as any)?._id || user?.schoolId?._id || '',
    },
    validationSchema: templateSchema,
    onSubmit: async (values) => {
      try {
        const payload: LetterTemplateV2FormData & { schoolId?: string } = {
          name: values.name.trim(),
          categoryId: values.categoryId,
          content: values.content,
        };
        if (isSystemAdmin) {
          payload.schoolId = selectedCenter || values.schoolId || (typeof editing?.schoolId === 'string' ? editing.schoolId : (editing?.schoolId as any)?._id);
        }
        if (editing?._id) {
          await letterTemplateService.updateLetterTemplate(editing._id, payload);
          enqueueSnackbar('Template updated', { variant: 'success' });
        } else {
          await letterTemplateService.createLetterTemplate(payload);
          enqueueSnackbar('Template created', { variant: 'success' });
        }
        closeDialog();
        await loadTemplates();
      } catch (err: any) {
        enqueueSnackbar(err.message || 'Operation failed', { variant: 'error' });
      }
    }
  });

  const pickedCategory = useMemo(
    () => categories.find(c => c._id === formik.values.categoryId) || null,
    // formik.values.categoryId is the dependency we actually care about; ESLint
    // can't see it through the useMemo from the formik reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, formik.values.categoryId]
  );
  const letterDetailPlaceholders = useMemo(
    () => buildLetterPlaceholders(pickedCategory),
    [pickedCategory]
  );

  const insertPlaceholder = (placeholder: string) => {
    const ta = contentRef.current;
    const current = formik.values.content;
    if (!ta) {
      formik.setFieldValue('content', current + placeholder);
      return;
    }
    const start = ta.selectionStart ?? current.length;
    const end = ta.selectionEnd ?? current.length;
    const next = current.substring(0, start) + placeholder + current.substring(end);
    formik.setFieldValue('content', next);
    setTimeout(() => {
      ta.focus();
      const pos = start + placeholder.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const openCreate = () => {
    if (isSystemAdmin && !selectedCenter) {
      enqueueSnackbar('Please select a center first', { variant: 'warning' });
      return;
    }
    setEditing(null);
    formik.resetForm({
      values: {
        name: '',
        categoryId: '',
        content: '',
        schoolId: isSystemAdmin ? selectedCenter : user?.schoolId?._id || '',
      }
    });
    setDialogOpen(true);
  };

  const openEdit = (t: LetterTemplateV2) => {
    setEditing(t);
    formik.resetForm({
      values: {
        name: t.name,
        categoryId: typeof t.categoryId === 'string' ? t.categoryId : t.categoryId._id,
        content: t.content,
        schoolId: typeof t.schoolId === 'string' ? t.schoolId : (t.schoolId as any)?._id,
      }
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    formik.resetForm();
  };

  const handleDelete = async () => {
    if (!menuTarget) return;
    try {
      await letterTemplateService.deleteLetterTemplate(menuTarget._id);
      enqueueSnackbar('Template deleted', { variant: 'success' });
      setDeleteOpen(false);
      setMenuTarget(null);
      await loadTemplates();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to delete template', { variant: 'error' });
    }
  };

  if (!canRead('letters')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', maxWidth: 420, border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Block sx={{ fontSize: 56, color: '#9e9e9e', mb: 2 }} />
          <Typography variant="h6" fontWeight={500}>Access Denied</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You don't have permission to view Letter Templates.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const renderCategoryChip = (catRef: LetterCategoryRef | string | undefined) => {
    if (!catRef) return null;
    const id = typeof catRef === 'string' ? catRef : catRef._id;
    const live = categoryById[id] || (typeof catRef === 'object' ? catRef as LetterCategoryRef : null);
    if (!live) return <Chip label="Unknown" size="small" variant="outlined" />;
    return (
      <Chip
        label={live.label}
        size="small"
        sx={severityChipSx(theme, (live as any).severity || 'neutral')}
        icon={<LabelOutlined sx={{ fontSize: 14 }} />}
      />
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Letter Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            Pre-written letter content tied to a category. Used when issuing letters to students.
          </Typography>
        </Box>
        {canWrite('letters') && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ borderRadius: 2 }}>
            Add Template
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        <Grid container spacing={2} alignItems="center">
          {isSystemAdmin && (
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Center</InputLabel>
                <Select
                  value={selectedCenter}
                  onChange={(e) => { setSelectedCenter(e.target.value); setPage(0); }}
                  label="Center"
                >
                  <MenuItem value="">All Centers</MenuItem>
                  {centers.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadTemplates} fullWidth>
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Templates Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Template Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                    <Assignment sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {categoryFilter ? 'No templates match your filter' : 'No letter templates found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map(t => (
                  <TableRow key={t._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{t.name}</Typography>
                    </TableCell>
                    <TableCell>{renderCategoryChip(t.categoryId)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.createdBy?.name || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(t.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuTarget(t); }}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Row menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              minWidth: 180,
              mt: 0.5,
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 6px 24px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
              '& .MuiList-root': { py: 0.5 },
              '& .MuiMenuItem-root': {
                px: 1.5, py: 0.75, gap: 1.25,
                fontSize: '0.825rem',
                color: 'text.primary',
                borderRadius: 1,
                mx: 0.5,
                '& .MuiListItemIcon-root': { minWidth: 0, color: 'text.secondary' },
                '& .MuiSvgIcon-root': { fontSize: 16 },
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) },
              },
            },
          },
        }}
      >
        {menuTarget && (
          <>
            <MenuItem onClick={() => { openEdit(menuTarget); setMenuAnchor(null); }}>
              <ListItemIcon><Edit /></ListItemIcon>
              <ListItemText primary="Edit template" />
            </MenuItem>
            <Divider sx={{ my: 0.5, mx: 1 }} />
            <MenuItem
              onClick={() => { setDeleteOpen(true); setMenuAnchor(null); }}
              sx={{
                '&&': {
                  color: theme.palette.error.main,
                  '& .MuiListItemIcon-root': { color: theme.palette.error.main },
                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) },
                },
              }}
            >
              <ListItemIcon><Delete /></ListItemIcon>
              <ListItemText primary="Delete template" />
            </MenuItem>
          </>
        )}
      </Menu>

      <ConfirmationDialog
        open={deleteOpen}
        onClose={() => { setDeleteOpen(false); setMenuTarget(null); }}
        onConfirm={handleDelete}
        title="Delete template?"
        message="Are you sure you want to delete this letter template? Already-issued letters keep their content."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        confirmColor="error"
      />

      {/* Template Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="lg" fullWidth>
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>
            {editing ? 'Edit Letter Template' : 'New Letter Template'}
          </Typography>
          <IconButton size="small" onClick={closeDialog}><Close /></IconButton>
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="name"
                  label="Template Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && (formik.errors.name as string)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="categoryId"
                    value={formik.values.categoryId}
                    onChange={formik.handleChange}
                    label="Category"
                  >
                    {categories.map(c => (
                      <MenuItem key={c._id} value={c._id}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            bgcolor: theme.palette[c.severity === 'positive' ? 'success' : c.severity === 'critical' ? 'error' : c.severity === 'caution' ? 'warning' : 'info'].main,
                          }} />
                          <span>{c.label}</span>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  name="content"
                  label="Template Content"
                  multiline
                  rows={10}
                  value={formik.values.content}
                  onChange={formik.handleChange}
                  error={formik.touched.content && Boolean(formik.errors.content)}
                  helperText={formik.touched.content && (formik.errors.content as string)}
                  placeholder="Start typing your letter template content here..."
                  inputRef={contentRef}
                />

                <Box sx={{
                  mt: 2, p: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  borderRadius: 2,
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                }}>
                  <Typography variant="subtitle2" fontWeight={600} color="primary.main" sx={{ mb: 1 }}>
                    Insert Placeholders
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {pickedCategory
                      ? <>Only placeholders for <b>{pickedCategory.label}</b> are shown — these are the fields the issuer will fill in.</>
                      : 'Click any placeholder to insert it at your cursor position. Pick a category to narrow this list to relevant fields.'}
                  </Typography>
                  <Grid container spacing={3}>
                    {COMMON_PLACEHOLDERS.map((grp) => (
                      <Grid size={{ xs: 12, md: 4 }} key={grp.group}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary"
                          sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {grp.group}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {grp.items.map(p => (
                            <Chip
                              key={p}
                              label={p}
                              size="small"
                              onClick={() => insertPlaceholder(p)}
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.dark',
                                fontFamily: 'monospace',
                                fontWeight: 500,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) }
                              }}
                            />
                          ))}
                        </Box>
                      </Grid>
                    ))}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary"
                        sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Letter Details {pickedCategory && <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled', textTransform: 'none' }}>(based on {pickedCategory.label})</Typography>}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {letterDetailPlaceholders.map(p => (
                          <Chip
                            key={p}
                            label={p}
                            size="small"
                            onClick={() => insertPlaceholder(p)}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: 'primary.dark',
                              fontFamily: 'monospace',
                              fontWeight: 500,
                              cursor: 'pointer',
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) }
                            }}
                          />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeDialog} color="inherit">Cancel</Button>
            <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
              {editing ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default LettersPage;
